import React, { useRef } from "react";
import { PageLayout } from "@Local/Components/Layout";
import { OverviewListLayout } from "@Local/Components/OverviewList";
import { Grid } from "@mui/material";
import { AutocompleteField, Chip, DateIntervalField, MenuItem, SelectField, Stack, TextField, Typography } from "@curves/ui-base";

import useOverview from "@Util/hook/useOverview";
import useOrderApi from "@Apis/useOrderApi";
import useOptions from "@Util/hook/useOptions";
import { Link } from "react-router-dom";
import { ORDERS_VIEW_ROUTER_URL } from "@Router/routerPath";
import { ORDER_STATUS_COLOR_MATCH } from "@Constants/appConfig";
import { formatCurrencyFn, isEmpty, refIsRequiredError } from "@Util/utils";
import { OverflowMenu } from "@Local/Components";
import DateTimeDisplay from "../Components/RowLabel/DateTimeDisplay";
import { filterMenu, getOrderMenuMap } from "@Local/Orders/Orders.util";
import OrdersDialogBox from "@Local/Orders/Componetnets/OrdersDialogBox";
/**
 * 預設搜尋參數
 * @type {Object}
 */
const defaultSearchParams = {
	pageIndex: 1,
	pageSize: 20,
	name: "",
	orderSourceType: 99,
	startDate: "",
	endDate: "",
	orderType: 52,
	status: 99,
	receiverType: 99,
	clubID: 0, // 0 代表全部
};
/**
 * 取得設備租賃訂單總覽表格欄位配置
 * @returns {Array<Object>} 表格欄位配置陣列
 */
const getDeviceRentalOrdersOverviewRowLabel = () => {
	return [
		{
			cellKey: "orderNo",
			headerLabel: "訂單",
			align: "center",
			width: "20",
			formatCell: (orderNo, { orderID, statusColor, statusText }) => {
				return (
					<Stack direction={"column"}>
						<Link className={"mb-1"} to={ORDERS_VIEW_ROUTER_URL(orderID)}>
							{orderNo}
						</Link>
						<Chip label={statusText} color={ORDER_STATUS_COLOR_MATCH[statusColor]} title={"訂單狀態"} />
					</Stack>
				);
			},
		},
		{
			cellKey: "receiverName",
			headerLabel: "購買人",
			width: "20",
			formatCell: (receiverName, { receiverTypeText }) => {
				return (
					<React.Fragment>
						<Typography variant={"subtitle1"} className={"p-0 font-weight-bold"}>
							{receiverName}
						</Typography>
						<Typography variant={"body2"} className={"mb-0 font-color-3"}>{`身份：${receiverTypeText}`}</Typography>
					</React.Fragment>
				);
			},
		},
		// { 目前只有智能，如有擴充其他設備再顯示
		//     cellKey: 'orderTypeText',
		//     headerLabel: '訂單類型',
		//     align: 'center',
		// },
		{
			cellKey: "clubName",
			headerLabel: "銷售店鋪",
			width: "12",
			align: "center",
		},
		{
			cellKey: "payablePrice",
			headerLabel: "訂單金額",
			align: "right",
			width: "15",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue, { symbol: "$" });
			},
		},
		{
			cellKey: "orderDate",
			headerLabel: "建立日期",
			formatCell: orderDate => {
				return <DateTimeDisplay date={orderDate} />;
			},
		},
		{
			cellKey: "more",
			headerLabel: "",
			width: 6.8,
			formatCell: (_, rowData, { dialogRef, refresh }) => {
				const menuData = filterMenu(
					getOrderMenuMap(rowData, {
						dialogRef: dialogRef,
						refresh: refresh,
					}),
				);
				return <OverflowMenu menuData={menuData} enabled={!isEmpty(menuData)} />;
			},
		},
	];
};
/**
 * 設備租賃訂單總覽頁面
 *
 * 功能：
 * - 顯示設備租賃訂單列表
 * - 提供多種搜尋和篩選條件
 * - 支援分頁瀏覽和訂單操作
 *
 * @component
 * @returns {JSX.Element} 設備租賃訂單總覽頁面
 */
const DeviceRentalOrdersOverviewPage = () => {
	const { getOrderOverviewOptionsApi, postOrdersOverviewAdvanceApi } = useOrderApi();
	const { options } = useOptions({
		getOptionsApi: getOrderOverviewOptionsApi,
		isFetchOnMount: true,
	});
	const { searchParams, overviewData, getOverviewData, updateOverviewPage } = useOverview({
		getOverviewApi: postOrdersOverviewAdvanceApi,
		defaultSearchParams: defaultSearchParams,
		enableQueryString: true,
		enableInfiniteScroll: false,
		isFetchOnMount: false,
	});
	const { list, pagination } = overviewData || {};
	const nameRef = useRef(null);
	const dateRangeRef = useRef(null);
	const clubRef = useRef(null);
	const orderStatusRef = useRef(null);
	const dialogRef = useRef(null);
	/**
	 * 取得搜尋參數
	 * @returns {Object} 搜尋參數物件
	 */
	const getSearchParams = () => {
		const params = defaultSearchParams;
		if (nameRef.current) params.name = nameRef.current.getResult() || "";
		if (orderStatusRef.current) params.status = orderStatusRef.current.getResult() || 99;
		if (clubRef.current) params.clubID = clubRef.current.getResult("value") || 0;
		if (dateRangeRef.current) {
			const { startDate, endDate } = dateRangeRef.current.getResult();
			params.startDate = startDate || "";
			params.endDate = endDate || "";
		}
		return params;
	};
	/**
	 * 處理搜尋表單提交
	 */
	const handleSubmit = () => {
		const isError = refIsRequiredError(nameRef, dateRangeRef, clubRef, orderStatusRef);
		if (isError) return;
		getOverviewData(getSearchParams());
	};
	return (
		<PageLayout>
			<PageLayout.Header title="設備租賃訂單總覽" />
			<OverviewListLayout>
				<OverviewListLayout.SearchBar onSubmit={handleSubmit} isShowActions>
					<Grid spacing={2} container>
						<Grid xs={8} item>
							<TextField
								label={"會員/編號"}
								ref={nameRef}
								key={searchParams?.name}
								defaultValue={searchParams.name}
								InputProps={{ placeholder: "請輸入訂單編號或購買人或會員編號" }}
								fullWidth
							/>
						</Grid>
						<Grid xs={4} item>
							<SelectField label={"訂單狀態"} ref={orderStatusRef} key={searchParams?.status} defaultValue={searchParams?.status} fullWidth required>
								{(options?.integrateStatusOptions || []).map(item => (
									<MenuItem key={item.value} value={item.value}>
										{item.text}
									</MenuItem>
								))}
							</SelectField>
						</Grid>
						<Grid xs={8} item>
							<DateIntervalField
								label={"建立日期"}
								ref={dateRangeRef}
								startDateProps={{
									key: searchParams?.startDate,
									defaultValue: searchParams?.startDate,
								}}
								endDateProps={{
									key: searchParams?.endDate,
									defaultValue: searchParams?.endDate,
								}}
								fullWidth
							/>
						</Grid>
						<Grid xs={4} item>
							<AutocompleteField
								key={`${searchParams?.clubID}_${(options?.clubOptions || []).length}`}
								label={"銷售店鋪"}
								inputProps={{ placeholder: "請輸入店名/店碼" }}
								ref={clubRef}
								options={options?.clubOptions || []}
								optionKeys={["text"]}
								primaryKey={"value"}
								defaultValue={searchParams?.clubID}
								required
								fullWidth
							/>
						</Grid>
					</Grid>
				</OverviewListLayout.SearchBar>
				<OverviewListLayout.Summary
					pageSortAndTotalProps={{
						pageIndex: pagination.pageIndex,
						pageSize: pagination.pageSize,
						totalCount: pagination.totalCount,
						isCurrentPageFirstIndex: true,
						isCurrentPageLastIndex: true,
					}}
				/>
				<OverviewListLayout.Table
					headerRow={getDeviceRentalOrdersOverviewRowLabel()}
					list={list || []}
					pageIndex={pagination?.pageIndex}
					totalPage={pagination?.totalPage}
					tableRowBodyProps={{
						dialogRef: dialogRef,
					}}
					refresh={() => getOverviewData(searchParams)}
					onChangePage={(e, pageIndex) => updateOverviewPage(pageIndex)}
					isPagination
				/>
			</OverviewListLayout>
			<OrdersDialogBox ref={dialogRef} />
		</PageLayout>
	);
};
export default DeviceRentalOrdersOverviewPage;
